import { useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import {
  RebalanceItem,
  RebalanceResolution,
  resolveRebalance,
} from 'src/pages/admin/services/xbitRelayRebalancesService';

import { RebalanceSummary } from './RebalanceSummary';
import { WalletSignerPicker, useWalletSigner } from './WalletSigner';
import { buildResolveRebalanceMessage } from './MessageBuilders';
import { REBALANCE_RESOLUTIONS } from './constants';
import { errorMessage, expectedAmountOf, isUserRejection } from './helpers';

type ResolveDialogProps = {
  record: RebalanceItem;
  onClose(): void;
};

export const ResolveDialog = ({ record, onClose }: ResolveDialogProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const signer = useWalletSigner();

  const [submitting, setSubmitting] = useState(false);
  const [resolution, setResolution] = useState<RebalanceResolution>('DONE');
  const [dstTxHash, setDstTxHash] = useState(record.dstTxHash || '');
  const [reason, setReason] = useState('');

  const isDone = resolution === 'DONE';
  const reasonEmpty = !reason.trim();
  // DONE needs the destination tx hash; FAILED sends '' — the field is required either way.
  const txHashMissing = isDone && !dstTxHash.trim();
  const busy = submitting || signer.connecting;

  const handleSignAndResolve = async () => {
    if (!signer.address || reasonEmpty || txHashMissing) return;
    setSubmitting(true);
    try {
      // Only the id, the action and the timestamp are signed. `resolution`, `reason`,
      // `dst_tx_hash` and `expected_amount` travel in the body and are validated server-side.
      // Stamped fresh so the signature stays inside the server-side freshness window.
      const timestampMs = Date.now();
      const expectedAmount = expectedAmountOf(record);
      const trimmedReason = reason.trim();
      const signedTxHash = isDone ? dstTxHash.trim() : '';

      const signature = await signer.signMessage(
        buildResolveRebalanceMessage({ id: record.id, timestampMs })
      );

      const { data } = await resolveRebalance(record.id, {
        admin_id: signer.address.toLowerCase(),
        resolution,
        reason: trimmedReason,
        dst_tx_hash: signedTxHash,
        expected_amount: expectedAmount,
        timestamp_ms: timestampMs,
        signature,
      });

      // Business failures — wrong signer, stale amount, bad status transition — arrive as
      // HTTP 200 with success: false.
      if (!data?.success) {
        notify(data?.message || t('module.xbitRelayHotWalletBalancer.resolveRejected'), {
          type: 'error',
        });
        refresh();
        return;
      }

      notify('module.xbitRelayHotWalletBalancer.resolveSuccess', { type: 'success' });
      onClose();
      refresh();
    } catch (error) {
      if (isUserRejection(error)) return;
      notify(errorMessage(error, t('module.xbitRelayHotWalletBalancer.resolveRejected')), {
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xbitRelayHotWalletBalancer.resolveTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">
            {t('module.xbitRelayHotWalletBalancer.resolveTerminalWarning')}
          </Alert>

          <RebalanceSummary record={record} />

          <Divider />

          <FormControl size="small" fullWidth required>
            <InputLabel>{t('module.xbitRelayHotWalletBalancer.resolution')}</InputLabel>
            <Select
              value={resolution}
              label={t('module.xbitRelayHotWalletBalancer.resolution')}
              onChange={(event) => setResolution(event.target.value as RebalanceResolution)}
              disabled={busy}
            >
              {REBALANCE_RESOLUTIONS.map((item) => (
                <MenuItem key={item} value={item}>
                  {t(`module.xbitRelayHotWalletBalancer.resolution_${item}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isDone && (
            <TextField
              label={t('module.xbitRelayHotWalletBalancer.dstTxHash')}
              value={dstTxHash}
              onChange={(event) => setDstTxHash(event.target.value)}
              size="small"
              fullWidth
              required
              error={txHashMissing}
              inputProps={{ style: { fontFamily: 'monospace' } }}
              helperText={t('module.xbitRelayHotWalletBalancer.dstTxHashHelp')}
              disabled={busy}
            />
          )}

          <TextField
            label={t('module.xbitRelayHotWalletBalancer.resolveReason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            error={reasonEmpty}
            helperText={t('module.xbitRelayHotWalletBalancer.reasonSignedHelp')}
            disabled={busy}
          />

          <Divider />

          <WalletSignerPicker signer={signer} busy={submitting} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={busy}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color={isDone ? 'success' : 'error'}
          onClick={handleSignAndResolve}
          disabled={!signer.address || busy || reasonEmpty || txHashMissing}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {t('module.xbitRelayHotWalletBalancer.signAndResolve')}
        </Button>
      </DialogActions>
    </>
  );
};
